import { syncMemberChange } from '../../../features/members/memberSync';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/users';
import { useUserStore } from '../../../stores/userStore';
import { captureBadgeActor, isBadgeActorCurrent } from '../../../features/badges/progressEvents';

const empty = () => ({ items: [], loading: true, error: false });
export default function useFollowList({
  nickname,
  viewer,
  followersCount,
  followingCount,
  onProfileUserUpdate,
  initialSnapshot,
}) {
  const client = useQueryClient();
  const [lists, setLists] = useState(
    initialSnapshot?.lists || { followers: empty(), following: empty() },
  );
  const [counts, setCounts] = useState(
    initialSnapshot?.counts || { followers: followersCount, following: followingCount },
  );
  const [busy, setBusy] = useState(null);
  const [mutationError, setMutationError] = useState(false);
  const session = useRef({
    token: localStorage.getItem('POSTPOP_AUTH_TOKEN'),
    actor: captureBadgeActor(),
  });
  const alive = useRef(false),
    pending = useRef(false),
    requests = useRef({}),
    revisions = useRef({});
  const profileUpdate = useRef(onProfileUserUpdate);
  profileUpdate.current = onProfileUserUpdate;
  const active = useCallback(
    () =>
      alive.current &&
      String(useUserStore.getState().user?.id) === String(viewer.id) &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === session.current.token &&
      (!session.current.actor || isBadgeActorCurrent(session.current.actor)),
    [viewer.id],
  );
  const begin = useCallback(
    (key) => {
      requests.current[key]?.abort();
      const controller = new AbortController();
      requests.current[key] = controller;
      const revision = (revisions.current[key] = (revisions.current[key] || 0) + 1);
      return {
        signal: controller.signal,
        isSessionCurrent: () =>
          active() && revisions.current[key] === revision && !controller.signal.aborted,
        suppressGlobalError: true,
      };
    },
    [active],
  );
  const load = useCallback(
    async (tab) => {
      if (!active()) return;
      const options = begin(tab);
      setLists((s) => ({ ...s, [tab]: { ...s[tab], loading: true, error: false } }));
      try {
        const response = await (
          tab === 'followers' ? usersApi.getFollowers : usersApi.getFollowing
        )(nickname, options);
        if (!options.isSessionCurrent()) return;
        if (!Array.isArray(response.data)) throw Error('Invalid follow list');
        setLists((s) => ({ ...s, [tab]: { items: response.data, loading: false, error: false } }));
      } catch {
        if (options.isSessionCurrent())
          setLists((s) => ({ ...s, [tab]: { ...s[tab], loading: false, error: true } }));
      }
    },
    [active, begin, nickname],
  );
  const loadCounts = useCallback(async () => {
    if (!active()) return;
    const options = begin('profile');
    try {
      const response = await usersApi.getUser(nickname, options);
      if (!options.isSessionCurrent() || response.data?.nickname !== nickname) return;
      const profile = response.data;
      setCounts((s) => ({
        followers: profile.followersCount ?? s.followers,
        following: profile.followingCount ?? s.following,
      }));
      profileUpdate.current?.((old) =>
        old?.nickname === nickname
          ? {
              ...old,
              followersCount: profile.followersCount ?? old.followersCount,
              followingCount: profile.followingCount ?? old.followingCount,
            }
          : old,
      );
    } catch {
      /* List results and existing profile counts remain usable. */
    }
  }, [active, begin, nickname]);
  useEffect(() => {
    alive.current = true;
    void load('followers');
    void load('following');
    void loadCounts();
    const controllers = requests.current;
    return () => {
      alive.current = false;
      Object.values(controllers).forEach((c) => c.abort());
    };
  }, [load, loadCounts]);
  const toggle = async (target) => {
    if (
      !active() ||
      pending.current ||
      target.id == null ||
      String(target.id) === String(viewer.id)
    )
      return;
    pending.current = true;
    setBusy(target.id);
    setMutationError(false);
    const options = begin('mutation');
    try {
      const response = await (target.isFollowing ? usersApi.unfollowUser : usersApi.followUser)(
        target.id,
        options,
      );
      if (!options.isSessionCurrent()) return;
      if (
        String(response.data?.id) !== String(target.id) ||
        typeof response.data?.isFollowing !== 'boolean'
      )
        throw Error('Invalid follow response');
      const updated = { ...target, ...response.data },
        own = viewer.nickname === nickname;
      // In-flight reads started before the mutation must never overwrite its result.
      ['followers', 'following', 'profile'].forEach((key) => requests.current[key]?.abort());
      const synced = await syncMemberChange(client, {
        viewer,
        target: updated,
        kind: 'follow',
        current: options.isSessionCurrent,
      });
      if (!synced || !options.isSessionCurrent()) return;
      setLists((s) =>
        Object.fromEntries(
          Object.entries(s).map(([tab, state]) => {
            let items = state.items.map((u) =>
              String(u.id) === String(updated.id) ? { ...u, ...updated } : u,
            );
            if (own && tab === 'following') {
              items = items.filter((u) => String(u.id) !== String(updated.id));
              if (updated.isFollowing) items.push(updated);
            }
            return [tab, { ...state, items, loading: false }];
          }),
        ),
      );
      if (own && Boolean(target.isFollowing) !== updated.isFollowing) {
        const delta = updated.isFollowing ? 1 : -1;
        setCounts((s) => ({
          ...s,
          following: Math.max(0, Number(s.following ?? followingCount ?? 0) + delta),
        }));
        profileUpdate.current?.((old) =>
          old?.nickname === nickname
            ? {
                ...old,
                followingCount: Math.max(
                  0,
                  Number(old.followingCount ?? followingCount ?? 0) + delta,
                ),
              }
            : old,
        );
      } else if (updated.nickname === nickname) {
        profileUpdate.current?.((old) =>
          old?.nickname === nickname ? { ...old, ...updated } : old,
        );
      }
      void loadCounts();
    } catch {
      if (options.isSessionCurrent()) setMutationError(true);
    } finally {
      pending.current = false;
      if (active()) setBusy(null);
    }
  };
  const refresh = (tab) => {
    if (pending.current) return;
    void load(tab);
    void loadCounts();
  };
  return { lists, counts, busy, mutationError, toggle, refresh };
}
